import { c as _c } from "react/compiler-runtime";
import React, { memo, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import useProductAttributes from 'data-hooks/useProductAttributes';
import usePDPPhotos from 'data-hooks/usePDPPhotos';
import { getPriceDetailsUtil } from 'data-hooks/utils';
import useModularPDPCart from 'data-hooks/useModularPDPCart';
import { useLocation } from 'react-router-dom';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import usePDPPrice from 'data-hooks/usePDPPrice';
import getProductImageAngle from 'commons/getProductImageAngle';
import contructHeroImageForNonConfigurableProduct from 'commons/contructHeroImageForNonConfigurableProduct';
import { getPriceValidUntil } from './utils';
import buildProductGroupJsonLd, { isNationallyOutOfStock, toJsonLdScriptContent } from './buildProductGroupJsonLd';
import usePdpIsModularBYOProduct from '../state/context/usePdpIsModularBYOProduct';
import usePdpSelectedOptionsVariantSku from '../state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
import usePDPBasicDetails from '../state/context/usePDPBasicDetails';
import usePDPOptions from '../state/context/usePDPOptions';
const PDPSEOTagsInner = ({
  isOutOfStock = false
}) => {
  // Keeps og:price stable if the price changes client-side after first render.
  // The JSON-LD does not use this; it prices itself from basicDetails.
  const initialPrice = useRef(null);
  const {
    data: attributesData
  } = useProductAttributes();
  const {
    data: priceData = {},
    loading: priceLoading
  } = usePDPPrice();
  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();
  const {
    data: modularCartData
  } = useModularPDPCart();
  const {
    data: photosData
  } = usePDPPhotos();
  const {
    data: basicDetails = {}
  } = usePDPBasicDetails();
  const {
    data: optionsData
  } = usePDPOptions();
  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();
  const location = useLocation();
  const priceDetails = useMemo(() => {
    if (!priceData || priceLoading) {
      return {};
    }
    if (pdpIsModularBYOProduct) {
      return {
        salePriceWithOptionsPrice: modularCartData?.modularTotalPrice
      };
    }
    return getPriceDetailsUtil(priceData?.price, priceData?.promotion, priceData?.optionPrice, priceData?.isClearance);
  }, [priceData, priceLoading, pdpIsModularBYOProduct, modularCartData?.modularTotalPrice]);
  const calculatedPrice = priceDetails.hasTieredDiscount ? priceDetails.leastTieredDiscountedPrice : priceDetails?.salePriceWithOptionsPrice;

  // Once calculatdPrice is truthy, store it as initialPrice. If
  // initialPrice.current is truthy, the value should not be updated.
  initialPrice.current = initialPrice.current || calculatedPrice;
  const price = initialPrice.current;
  const basicDetailsName = basicDetails?.name;
  const basicDetailsSku = basicDetails?.sku;
  const attributesDescription = attributesData?.SEOAttributes?.meta_description;

  // ref: https://stitchindustries.atlassian.net/browse/CON-6035
  // From basicDetails, not usePDPPrice: the latter is gated on the stock query
  // and would be absent on the server.
  const priceValidUntil = useMemo(() => getPriceValidUntil(basicDetails?.meta?.promotion), [basicDetails?.meta?.promotion]);

  // Last-resort image. The selected photo is client-only and would make the
  // server and client disagree, so fall back to the same constructed hero URL
  // usePDPPhotos builds for products that carry no hero_images.
  const fallbackHeroImage = useMemo(() => {
    const fromHeroImages = basicDetails?.hero_images?.[0]?.img?.url;
    if (fromHeroImages) {
      return fromHeroImages;
    }
    if (!basicDetails?.name || !basicDetails?.id) {
      return '';
    }
    return contructHeroImageForNonConfigurableProduct(basicDetails.name, basicDetails.id, getProductImageAngle({
      product: basicDetails
    }))?.transparent_hero_image_ts || '';
  }, [basicDetails]);
  const heroImageByUpholsteryName = useMemo(() => {
    const heroImages = basicDetails?.hero_images || [];
    return heroImages.reduce((map, heroImg) => {
      if (heroImg?.upholstery_name) {
        const key = heroImg.upholstery_name.toLowerCase().trim();
        if (!map[key]) {
          map[key] = heroImg.img?.url;
        }
      }
      return map;
    }, {});
  }, [basicDetails?.hero_images]);

  // From server-seeded basicDetails rather than the client-only attributes
  // query, so the markup can render during SSR (CON-8772).
  const canonicalURL = useMemo(() => {
    const urlKey = basicDetails?.attributes_min?.urlKey;
    if (typeof urlKey === 'string' && urlKey.startsWith('/')) {
      // Match the app's trailingSlash: true config.
      const path = urlKey.endsWith('/') ? urlKey : `${urlKey}/`;
      return `https://joybird.com${path}`;
    }
    return attributesData?.SEOAttributes?.canonicalURL;
  }, [basicDetails?.attributes_min?.urlKey, attributesData?.SEOAttributes?.canonicalURL]);
  const metaDescription = useMemo(() => {
    const fromBasicDetails = (basicDetails?.attributes || []).find(attribute => attribute?.code === 'meta_description')?.value;
    return fromBasicDetails ?? attributesDescription;
  }, [basicDetails?.attributes, attributesDescription]);
  const jsonLD = useMemo(() => buildProductGroupJsonLd({
    name: basicDetailsName,
    description: metaDescription,
    sku: basicDetailsSku,
    canonicalURL,
    options: optionsData?.options,
    priceObj: basicDetails?.meta?.price,
    promotion: basicDetails?.meta?.promotion,
    priceValidUntil,
    // Not the isOutOfStock prop: that is derived from per-ZIP stock, so it
    // is absent on the server and can flip after hydration.
    isOutOfStock: isNationallyOutOfStock(basicDetails),
    heroImageByUpholsteryName,
    fallbackImage: fallbackHeroImage
  }), [basicDetailsName, metaDescription, basicDetailsSku, canonicalURL, optionsData?.options, basicDetails, priceValidUntil, heroImageByUpholsteryName, fallbackHeroImage]);
  const productNameFromRouterState = location?.state?.name;
  const pageTitle = basicDetails?.name || productNameFromRouterState || '';
  const jsonLDString = useMemo(() => toJsonLdScriptContent(jsonLD), [jsonLD]);
  return <>
      <Helmet>
        <title>{pageTitle ? `${pageTitle} | Joybird` : `Joybird`}</title>

        <meta name="description" content={attributesData?.SEOAttributes?.meta_description || ''} />

        <meta name="keywords" content={attributesData?.SEOAttributes?.meta_keywords || ''} />

        <meta property="og:type" content="product" />
        <meta property="og:title" content={pageTitle} />

        <meta property="og:description" content={attributesData?.SEOAttributes?.meta_description || ''} />

        <meta property="og:price:amount" content={price || ''} />
        <meta property="og:price:currency" content="USD" />

        <meta property="og:availability" content={isOutOfStock ? 'Out of Stock' : 'Ready to Ship'} />

        {/* og:url only. The canonical link is emitted by the route's
          generateMetadata so it lands in the prerendered shell rather than
          being injected client-side by Helmet, which also removes the
          duplicate <link id="canonical"> PDPs used to render (CON-8595). */}
        {attributesData?.SEOAttributes?.canonicalURL && <meta property="og:url" content={attributesData?.SEOAttributes?.canonicalURL} />}

        {photosData?.selectedPhoto?.src && <meta property="og:image" content={photosData?.selectedPhoto?.src} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${pageTitle} | Joybird`} />

        <meta name="twitter:description" content={attributesData?.SEOAttributes?.meta_description?.value || ''} />

        <meta data-rh="true" name="sku" content={`${basicDetails?.sku}${selectedOptionsVariantSku}`} />

        {photosData?.selectedPhoto?.src && <meta name="twitter:image" content={photosData.selectedPhoto.src} />}
      </Helmet>

      {/* Not in <Helmet>: Helmet only writes to document.head on the client, so
          this would be absent from the server response. Google accepts JSON-LD
          anywhere in the document, unlike a canonical. */}
      {jsonLDString && <script type="application/ld+json"
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{
      __html: jsonLDString
    }} />}
    </>;
};
PDPSEOTagsInner.propTypes = {
  isOutOfStock: PropTypes.bool
};
const PDPSEOTags = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <SuspenseWithLoader><PDPSEOTagsInner {...props} /></SuspenseWithLoader>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default memo(PDPSEOTags);